<template>
    <div
        class="bg-player-background overflow-hidden rounded-[16px] player-container sticky top-0 left-0 right-0 z-10 p-6 max-[320px]:p-4"
    >
        <div class="flex flex-wrap sm:flex-nowrap items-center">
            <div v-if="ccActive && isMobile && !showCtaCard">
                <div v-if="loadingTranscription" class="flex justify-center items-center py-4">
                    <span class="inline-block w-6 h-6 border-2 border-player-accent border-t-transparent rounded-full animate-spin"></span>
                    <span class="ml-2 text-xs text-white">{{ __('Loading...') }}</span>
                </div>
                <div v-else-if="transcriptionEntries.length === 0" class="text-center text-white text-xs">
                    {{ __('No transcript found') }}
                </div>
                <div v-else ref="mobileTranscriptContainer" class="overflow-hidden min-h-[180px] max-h-[180px]">
                    <ul>
                        <li
                            v-for="(entry, idx) in transcriptionEntries"
                            :key="idx+'-mobile'"
                            :ref="idx === currentTranscriptIndex ? activeTranscriptLine : null"
                            :class="[
                                'transition-colors',
                                idx === currentTranscriptIndex ? 'transcript-line-active' : '',
                            ]"
                        >
                        
                            <div
                                v-if="idx === currentTranscriptIndex - 1"
                                class="text-[12px] leading-5 text-white min-h-[60px] max-h-[60px] overflow-hidden line-clamp-3 flex items-end"
                            >
                                {{ entry.text }}
                            </div>
                            <div
                                v-else-if="idx === currentTranscriptIndex"
                                class="text-[13px] leading-5 font-semibold text-player-contrast min-h-[20px] max-h-[60px] overflow-hidden line-clamp-3"
                                :class="[idx === 0 ? 'mt-[60px]' : '', transcriptionEntries.length - 1 === idx ? 'mb-[180px]' : '']"
                            >
                                {{ entry.text }}
                            </div>
                            <div
                                v-else-if="idx === currentTranscriptIndex + 1"
                                class="text-[12px] leading-5 text-white min-h-[60px] max-h-[60px] overflow-hidden line-clamp-3 flex items-start"
                            >
                                {{ entry.text }}
                            </div>
                            <div
                                v-else
                                class="text-[12px] leading-5 text-white min-h-[10px] max-h-[10px] overflow-hidden line-clamp-3"
                            >
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <div
                v-if="showCtaCard"
                class="w-full justify-center sm:w-auto sm:pr-8 mb-8 sm:mb-0 shrink-0 hidden sm:flex"
            >
                <CtaCard
                    part="image"
                    :ctas="currentEpisode.ctas"
                    :seek="currentTime"
                    @clicked="onCtaClicked"
                />
            </div>

            <div v-else class="w-full justify-center sm:w-auto sm:pr-8 mb-8 sm:mb-0 shrink-0 hidden sm:flex">
                <img
                    v-if="currentEpisode"
                    :src="art"
                    class="h-[160px] sm:h-[192px] w-[160px] sm:w-[192px] aspect-square rounded-[6px]"
                    alt=""
                />
            </div>
            <div class="w-full min-w-0">
                <div v-if="showCtaCard" class="w-full min-w-0 sm:hidden mb-1">
                    <CtaCard
                        part="mobile"
                        :ctas="currentEpisode.ctas"
                        :seek="currentTime"
                        @clicked="onCtaClicked"
                    />
                </div>

                <div v-if="showCtaCard" class="hidden w-full relative max-w-full sm:block mb-1">
                    <CtaCard
                        part="header"
                        :ctas="currentEpisode.ctas"
                        :seek="currentTime"
                        @clicked="onCtaClicked"
                    />
                </div>

                <div v-if="!showCtaCard" class="w-full relative max-w-full">
                    <div :class="['font-bold mb-2 w-full min-w-0', ccActive ? 'mb-1' : '']">
                        <p
                            v-if="currentEpisode"
                            :class="[
                                'text-white font-medium pr-[100px] break-words',
                                ccActive ? 'text-[11px]' : 'text-[12px] mb-3'
                            ]"
                            :title="podcast.title"
                            :aria-label="podcast.title"
                        >
                            {{ podcast.title }}
                        </p>
                        <h1
                            v-if="currentEpisode"
                            :class="[
                                'font-bold break-words max-sm:truncate',
                                ccActive ? 'text-[15px] text-white' : 'text-[18px] text-player-contrast',
                                currentEpisode?.ctas?.length ? 'pr-28' : '',
                            ]"
                            :title="currentEpisode.title"
                        >
                            <template v-if="podcast.season">
                                S{{ currentEpisode.season }} / E{{ currentEpisode.episode }}
                            </template>
                            {{ currentEpisode.title }}
                        </h1>
                    </div>

                    <div class="shrink-0 absolute right-0 top-0" v-if="podcast?.branding">
                        <a href="https://www.springcast.io" target="_blank" rel="noopener" class="width: 84px; height: 18px">
                            <img src="/img/springcast_logo.svg" alt="Springcast" width="84" height="18" v-if="logoColor === 'light'" />
                            <img src="/img/springcast_logo_dark.svg" alt="Springcast" width="84" height="18" v-else />
                        </a>
                    </div>

                    <div
                        v-if="!callToActionEnabled && currentEpisode?.ctas?.length"
                        class="pointer-events-none absolute right-0 top-7 z-20 sm:top-8"
                    >
                        <CtaBlock
                            class="pointer-events-auto"
                            :ctas="currentEpisode.ctas"
                            :seek="currentTime"
                            @clicked="onCtaClicked"
                        />
                    </div>
                </div>

                <!-- Transcription display below titles -->
                <div v-if="ccActive && !isMobile && !showCtaCard">
                    <div v-if="loadingTranscription" class="flex justify-center items-center py-4">
                        <span class="inline-block w-6 h-6 border-2 border-player-accent border-t-transparent rounded-full animate-spin"></span>
                        <span class="ml-2 text-xs text-white">{{ __('Loading...') }}</span>
                    </div>
                    <div v-else-if="transcriptionEntries.length === 0" class="text-center text-white text-xs">{{ __('No transcript found') }}</div>
                    <div v-else ref="desktopTranscriptContainer" class="overflow-hidden" style="height: 4rem;">
                        <ul>
                            <li
                                v-for="(entry, idx) in transcriptionEntries"
                                :key="idx"
                                :class="[
                                    'transition-colors duration-75',
                                    idx === currentTranscriptIndex ? 'transcript-line-active' : '',
                                ]"
                            >
                                <div
                                    v-if="idx === currentTranscriptIndex"
                                    class="text-[12px] leading-snug font-medium text-player-contrast mb-1 min-h-[4em] max-h-[4em] overflow-hidden line-clamp-3 flex items-end"
                                >
                                    {{ entry.text }}
                                </div>
                                <div
                                    v-else-if="idx === currentTranscriptIndex + 1"
                                    class="text-[12px] leading-tight text-white min-h-[1.4em] max-h-[1.4em] overflow-hidden line-clamp-1"
                                >
                                    {{ entry.text }}
                                </div>
                                <div
                                    v-else
                                    :class="[
                                        'text-[12px] leading-tight text-gray-300 opacity-90 overflow-hidden',
                                        'line-clamp-1 min-h-[1em] max-h-[1em]',
                                        'max-[640px]:line-clamp-3 max-[640px]:min-h-[4em] max-[640px]:max-h-[4em]'
                                    ]"
                                >
                                    {{ entry.text }}
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <Timeline 
                    @ended="finalized" 
                    @scroll="scroll" 
                    @timeupdate="saveProgress" 
                    :podcast="podcast" 
                    :track="currentEpisode"
                    :start-at="startAt"
                    :hide-times="showCtaCard"
                    :compact="showCtaCard"
                    ref="timeline" 
                    @loaded="setProgressIfExists"
                />

                <EpisodeControls
                    :playing="timeline?.isPlaying"
                    :muted="timeline?.isMuted"
                    :compact="showCtaCard"
                    :ccActive="ccActive && !showCtaCard"
                    :hasTranscription="showCtaCard ? false : hasTranscription"
                    :show-share="podcast.share"
                    :seek="timeline?.currentTime ?? 0"
                    :episode-hash-id="currentEpisode?.hash_id"
                    @play="play"
                    @pause="pause"
                    @mute="mute"
                    @unmute="unmute"
                    @skipSeconds="skip"
                    @backSeconds="back"
                    @playbackRate="playbackRate"
                    @toggle-cc="toggleCC"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import __ from "../helpers";
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import EpisodeControls from "./EpisodeControls.vue";
import Timeline from "./Timeline.vue";
import CtaBlock from "./CtaBlock.vue";
import CtaCard from "./CtaCard.vue";
import { brightnessByColor, resolveActiveCta } from "../helpers.js";
import axios from "axios";
import { usePage } from "@inertiajs/vue3";
import { shiftCues } from "../../Shared/vtt.js";

const props = defineProps({
    podcast: {
        type: Object,
        required: true,
    },
    colors: Object,
    startAt: {
        type: [Number, String, null],
        required: false,
    },
});

const emit = defineEmits([
    'finalized',
    'scroll', 
    'saveProgress',
    'setProgressIfExists',
    'play',
    'pause',
    'mute',
    'unmute',
    'skip',
    'back',
    'playbackRate',
    'cta',
]);

const timeline = ref(null);

const currentEpisode = computed(() => Object.values(props.podcast.episodes)[0]);

const logoColor = computed(() => {
    const brightness = brightnessByColor(props.colors.player_background);
    if (brightness > 100) {
        return 'dark'
    }
    return 'light'
});

const art = computed(() => {
    return currentEpisode.value?.artwork;
});

// --- Transcription logic ---
const ccActive = ref(false);
const transcription = ref("");
const transcriptionEntries = ref([]);
const loadingTranscription = ref(false);
let fetchTranscriptionCancel = null;

const currentTime = computed(() => timeline.value?.currentTime ?? 0);

const callToActionEnabled = computed(() => !!props.podcast.call_to_action);

const activeCta = computed(() => {
    if (!callToActionEnabled.value || !currentEpisode.value?.ctas?.length) {
        return null;
    }

    return resolveActiveCta(currentTime.value, currentEpisode.value.ctas);
});

const showCtaCard = computed(() => activeCta.value !== null);

function onCtaClicked(ctaId) {
    emit('cta', ctaId);
}

const currentTranscriptIndex = computed(() => {
    let idx = -1;
    for (let i = 0; i < transcriptionEntries.value.length; i++) {
        if (currentTime.value >= transcriptionEntries.value[i].start) {
            idx = i;
        } else {
            break;
        }
    }
    return idx;
});

const hasTranscription = computed(() => {
    return transcriptionEntries.value.length > 0 && !loadingTranscription.value;
});

const activeTranscriptLine = ref(null);
const mobileTranscriptContainer = ref(null);
const desktopTranscriptContainer = ref(null);

function scrollActiveLineIntoContainer() {
    const container = isMobile.value ? mobileTranscriptContainer.value : desktopTranscriptContainer.value;
    if (!container) return;
    const el = activeTranscriptLine.value?.[0] || container.querySelector('.transcript-line-active');
    if (!el) return;
    const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    const top = isMobile.value
        ? offset - container.clientHeight / 2 + el.clientHeight / 2
        : offset;
    container.scrollTo({ top, behavior: 'smooth' });
}

const page = usePage();

async function fetchTranscription(silent = false) {
    if (!silent) {
        transcription.value = "";
        transcriptionEntries.value = [];
        loadingTranscription.value = true;
    }
    const episode = currentEpisode.value;
    if (!episode) {
        if (!silent) {
            loadingTranscription.value = false;
        }
        return;
    }
    // Cancel token for axios
    let cancel;
    fetchTranscriptionCancel = () => {
        if (cancel) cancel();
    };
    try {
        const url = route("podcast.transcription", {
            podcast: props.podcast.hash_id,
            episode: episode.hash_id,
            // Members-only show: the website's member signature (mexp/msig)
            ...(page.props.memberQuery ?? {}),
        });
        const response = await axios.get(url, {
            cancelToken: new axios.CancelToken(function executor(c) {
                cancel = c;
            })
        });
        transcription.value = response.data;
        // The VTT is on the original timeline; the audio is the assembled mp3
        // with dynamic-content inserts, so move the cues onto it.
        transcriptionEntries.value = shiftCues(parseVTT(response.data), episode.timeline?.insertions);
    } catch (e) {
        if (axios.isCancel && axios.isCancel(e)) {
            // cancelled, do nothing
        } else {
            if (!silent) {
                transcription.value = "Failed to load transcription.";
            }
            transcriptionEntries.value = [];
        }
    }
    if (!silent) {
        loadingTranscription.value = false;
    }
    loadingTranscription.value = false;
    fetchTranscriptionCancel = null;
}

function parseVTT(vtt) {
    const lines = vtt.split('\n');
    const entries = [];
    let i = 0;
    while (i < lines.length) {
        if (lines[i].includes('-->')) {
            const [start, end] = lines[i].split(' --> ').map(s => s.trim());
            let text = '';
            i++;
            while (lines[i] && !lines[i].includes('-->')) {
                text += lines[i] + ' ';
                i++;
            }
            // Remove speaker tags like <v Speaker A> and </v>
            const cleanText = text.replace(/<v[^>]*>/g, '').replace(/<\/v>/g, '').trim();
            entries.push({
                start: toSeconds(start),
                end: toSeconds(end),
                text: cleanText
            });
        } else {
            i++;
        }
    }
    return entries;
}

function toSeconds(time) {
    const parts = time.split(':');
    let seconds = 0;
    if (parts.length === 3) {
        seconds = (+parts[0]) * 3600 + (+parts[1]) * 60 + parseFloat(parts[2].replace(',', '.'));
    } else if (parts.length === 2) {
        seconds = (+parts[0]) * 60 + parseFloat(parts[1].replace(',', '.'));
    }
    return seconds;
}

function toggleCC() {
    if (ccActive.value && fetchTranscriptionCancel) {
        fetchTranscriptionCancel();
        loadingTranscription.value = false;
    }
    ccActive.value = !ccActive.value;
    if (ccActive.value && transcriptionEntries.value.length === 0) {
        fetchTranscription(); // Normal mode to show loading state
    } else if (ccActive.value && currentTranscriptIndex.value >= 0) {
        nextTick(scrollActiveLineIntoContainer);
    }
}

watch(currentEpisode, () => {
    // Reset transcription when episode changes
    transcription.value = "";
    transcriptionEntries.value = [];
    loadingTranscription.value = false;
    ccActive.value = false;
    fetchTranscriptionCancel = null;
    
    fetchTranscription(true);
});

function finalized() {
    emit('finalized');
}

function scroll() {
    emit('scroll');
}

function saveProgress() {
    emit('saveProgress');
}

function setProgressIfExists() {
    emit('setProgressIfExists');
}

function play() {
    emit('play');
}

function pause() {
    emit('pause');
}

function mute() {
    emit('mute');
}

function unmute() {
    emit('unmute');
}

function skip(seconds) {
    emit('skip', seconds);
}

function back(seconds) {
    emit('back', seconds);
}

function playbackRate(rate) {
    emit('playbackRate', rate);
}

watch(currentTranscriptIndex, async () => {
    await nextTick();
    scrollActiveLineIntoContainer();
});

const isMobile = ref(window.matchMedia('(max-width: 640px)').matches);

function handleResize() {
    isMobile.value = window.matchMedia('(max-width: 640px)').matches;
}

onMounted(() => {
    window.addEventListener('resize', handleResize);
    fetchTranscription(true); // Silent mode for availability check
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    fetchTranscription();
});

defineExpose({
    timeline
});
</script>

<style scoped>
/* Disable sticky positioning at high zoom levels (small viewport heights) */
@media (max-height: 600px) {
    .player-container {
        position: static !important;
    }
}
</style> 